import React, { ReactNode } from 'react'
import { Button, Dialog } from '@atoms'
import { AppAlerts } from '@components/molecules/Alerts/AppAlerts'
import type { DialogSize } from '@atoms/Dialog'
import { scopedTranslation } from '@frontend/utils/I18n'

const t = scopedTranslation('shared.buttons')

type FormDialogProps = {
  children: ReactNode
  open: boolean
  onOpenChange: (open: boolean) => void
  size?: DialogSize
  testId?: string
  preventOutsideClick?: boolean
}

function FormDialogRoot({
  children,
  open,
  onOpenChange,
  size = 'xl',
  testId,
  preventOutsideClick = false
}: FormDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        data-testid={testId}
        scroll={true}
        size={size}
        onPointerDownOutside={preventOutsideClick ? (e) => e.preventDefault() : undefined}
      >
        {children}
      </Dialog.Content>
    </Dialog.Root>
  )
}

type FormDialogTitleProps = {
  children: ReactNode
  alertsPath?: string
}

function FormDialogTitle({ children, alertsPath }: FormDialogTitleProps) {
  return (
    <Dialog.Header>
      <Dialog.Title>{children}</Dialog.Title>
      {alertsPath && (
        <div className="tw-font-medium tw-pt-4">
          <AppAlerts alertsPath={alertsPath} />
        </div>
      )}
    </Dialog.Header>
  )
}

type FormDialogFormProps = {
  children: ReactNode
  onSubmit: (e: React.FormEvent) => void
}

function FormDialogForm({ 
  children, 
  onSubmit
}: FormDialogFormProps) {
  return (
    <form onSubmit={onSubmit}>
      {children}
    </form>
  )
}

type FormDialogBodyProps = {
  children: ReactNode
}

function FormDialogBody({ 
  children, 
}: FormDialogBodyProps) {
  return (
    <div className='tw-pb-5 tw-space-y-4' style={{ overflow: 'visible', height: 'auto' }}>
      {children}
    </div>
  )
}

type FormDialogFooterProps = {
  submitText?: string
  showCancel?: boolean
  showSubmit?: boolean
  isLoading?: boolean
}

function FormDialogFooter({
  submitText = t('submit'),
  showCancel = true,
  showSubmit = true,
  isLoading = false,
}: FormDialogFooterProps) {
  return (
    <Dialog.Footer className='tw-gap-2 sm:tw-gap-0'>
      {showCancel && (
        <Dialog.Close asChild>
          <Button type="button" size="xl" rank="tertiary">
            {t('cancel')}
          </Button>
        </Dialog.Close>
      )}
      {showSubmit && (
        <Button 
          type="submit" 
          size="xl"
          rank="primary"
          disabled={isLoading}
        >
          {submitText}
        </Button>
      )}
    </Dialog.Footer>
  )
}

export const FormDialog = {
  Root: FormDialogRoot,
  Title: FormDialogTitle,
  Form: FormDialogForm,
  Body: FormDialogBody,
  Footer: FormDialogFooter,
}
